import React, { useState, useEffect, useRef } from 'react';
import { Bot, Mic, StopCircle, Volume2, Loader, VolumeX, Volume, CirclePause  } from 'lucide-react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { modelServicePost, modelServiceUpload } from '../../utils/modelService';

const formatText = (text) => {
    if (!text) return '';
    
    return text
        // Handle bold text **text**
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Handle bullet points (both * and •)
        .replace(/^\* (.+)$/gm, '<div style="margin-left: 16px; margin-bottom: 8px;">• $1</div>')
        .replace(/^• (.+)$/gm, '<div style="margin-left: 16px; margin-bottom: 8px;">• $1</div>')
        // Handle line breaks for proper spacing
        .replace(/\n\n/g, '<br><br>')
        .replace(/\n/g, '<br>');
};

function ChatModal({ isOpen, setIsOpen }) {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([]); // For RAG service chat history
    const [isLoading, setIsLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [audioStates, setAudioStates] = useState({}); // Track audio state for each message
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRefs = useRef({}); // Store audio elements for each message
    const messagesEndRef = useRef(null); // Reference for auto-scrolling

    // Send message to RAG API
    const sendMessageToAPI = async (message) => {
        try {
            const response = await modelServicePost('/generate', {
                message: message,
                chat_history: chatHistory,
                files: []
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error sending message to RAG service:', error);
            return null;
        }
    };

    const handleSendMessage = async () => {
        if (inputMessage.trim()) {
            try {
                // Add user message to UI and chat history
                const userMessage = {
                    id: Date.now(),
                    text: inputMessage,
                    isUser: true
                };
                setMessages(prev => [...prev, userMessage]);
                
                // Add to chat history for RAG service
                const newHistoryEntry = {
                    sender: 'user',
                    content: inputMessage
                };
                setChatHistory(prev => [...prev, newHistoryEntry]);
                
                const currentMessage = inputMessage;
                setInputMessage('');
                setIsLoading(true);

                // Send to RAG API and get response
                const apiResponse = await sendMessageToAPI(currentMessage);
                
                if (apiResponse && apiResponse.length > 0 && apiResponse[0].content) {
                    const responseText = apiResponse[0].content;
                    const messageId = Date.now() + 1;
                    
                    const aiMessage = {
                        id: messageId,
                        text: responseText,
                        isUser: false
                    };
                    setMessages(prev => [...prev, aiMessage]);
                    
                    // Add AI response to chat history
                    setChatHistory(prev => [...prev, {
                        sender: 'assistant',
                        content: responseText
                    }]);
                    
                    // Initialize audio state for this message
                    setAudioStates(prev => ({
                        ...prev,
                        [messageId]: { 
                            status: 'idle', // idle, loading, ready, playing
                            audioUrl: null
                        }
                    }));
                } else {
                    const errorMessage = {
                        id: Date.now() + 1,
                        text: "I apologize, but I couldn't process that request. Please try again.",
                        isUser: false
                    };
                    setMessages(prev => [...prev, errorMessage]);
                }
            } catch (error) {
                console.error('Error in handleSendMessage:', error);
                const errorMessage = {
                    id: Date.now() + 1,
                    text: "Sorry, there was an error processing your message. Please try again.",
                    isUser: false
                };
                setMessages(prev => [...prev, errorMessage]);
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Auto scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Speech-to-Text: Start recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Try to find a supported MIME type
            const mimeType = MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : MediaRecorder.isTypeSupported('audio/mp4')
                    ? 'audio/mp4'
                    : 'audio/wav';
            mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
            audioChunksRef.current = [];
    
            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };
    
            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                await transcribeAudio(audioBlob);
                
                // Release the microphone
                stream.getTracks().forEach(track => track.stop());
            };
    
            mediaRecorderRef.current.start(100);
            setIsRecording(true);
        } catch (error) {
            console.error('Error starting recording:', error);
            alert('Could not access microphone. Please check your permissions.');
        }
    };

    // Speech-to-Text: Stop recording
    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    // Speech-to-Text: Transcribe audio using Gemini via model service
    const transcribeAudio = async (audioBlob) => {
        try {
            setIsLoading(true);
            
            const formData = new FormData();
            // Use the correct file extension based on the MIME type
            const fileExtension = audioBlob.type.includes('webm') ? 'webm' : 
                                  audioBlob.type.includes('mp4') ? 'm4a' : 'wav';
            formData.append('audio', audioBlob, `recording.${fileExtension}`);
            
            const response = await modelServiceUpload('/transcribe', formData);
            
            const data = await response.json();
            
            if (data.text) {
                setInputMessage(data.text);
            } else {
                console.error('Transcription failed:', data);
            }
        } catch (error) {
            console.error('Error transcribing audio:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Text-to-Speech: Generate or control audio playback
    const handleAudio = async (messageId, text) => {
        // Get current state
        const currentState = audioStates[messageId] || { status: 'idle', audioUrl: null };
        
        // If already playing, pause
        if (currentState.status === 'playing') {
            if (audioRefs.current[messageId]) {
                audioRefs.current[messageId].pause();
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { ...currentState, status: 'ready' }
                }));
            }
            return;
        }
        
        // If paused/ready and we have audio, play it
        if (currentState.status === 'ready' && currentState.audioUrl) {
            if (audioRefs.current[messageId]) {
                audioRefs.current[messageId].play();
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { ...currentState, status: 'playing' }
                }));
            }
            return;
        }
        
        // If not generated yet, generate new audio
        if (currentState.status === 'idle' || !currentState.audioUrl) {
            try {
                // Update status to loading
                setAudioStates(prev => ({
                    ...prev, 
                    [messageId]: { ...currentState, status: 'loading' }
                }));
                
                const response = await modelServicePost('/tts', {
                    text: text,
                    voice: 'Kore',
                    style: 'normal',
                    pace: 'normal'
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to generate speech: ${response.statusText}`);
                }
                
                const audioBlob = await response.blob();
                const audioUrl = URL.createObjectURL(audioBlob);
                
                // Create and setup audio element
                if (!audioRefs.current[messageId]) {
                    audioRefs.current[messageId] = new Audio(audioUrl);
                    audioRefs.current[messageId].onended = () => {
                        setAudioStates(prev => ({
                            ...prev,
                            [messageId]: { ...prev[messageId], status: 'ready' }
                        }));
                    };
                } else {
                    audioRefs.current[messageId].src = audioUrl;
                }
                
                // Update status and store URL
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { status: 'playing', audioUrl }
                }));
                
                // Play the audio
                audioRefs.current[messageId].play();
            } catch (error) {
                console.error('Error generating or playing speech:', error);
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { ...currentState, status: 'idle' }
                }));
            }
        }
    };

    // Get appropriate audio icon based on status
    const getAudioIcon = (messageId) => {
        const state = audioStates[messageId];
        if (!state) return <Volume2 size={16} />;
        
        switch (state.status) {
            case 'loading':
                return <Loader size={16} className="animate-spin" />;
            case 'playing':
                return <CirclePause size={16} />;
            case 'ready':
            case 'idle':
            default:
                return <Volume2 size={16} />;
        }
    };

    // Initialize chat when modal opens
    useEffect(() => {
        const initializeChat = async () => {
            if (isOpen) {
                setMessages([]); // Clear existing messages
                setChatHistory([]); // Clear chat history
                setAudioStates({});  // Clear audio states
                
                // Set initial welcome message
                setMessages([{
                    id: 1,
                    text: "Chat with me about anything, I am JPRO. Terms & Conditions",
                    isUser: false
                }]);
            }
        };

        initializeChat();

        // Clean up audio and recording when modal closes
        return () => {
            if (mediaRecorderRef.current && isRecording) {
                mediaRecorderRef.current.stop();
                setIsRecording(false);
            }
            
            // Stop and clean up all audio elements
            Object.keys(audioRefs.current).forEach(key => {
                if (audioRefs.current[key]) {
                    audioRefs.current[key].pause();
                    audioRefs.current[key].src = '';
                }
            });
            audioRefs.current = {};
            setAudioStates({});
        };
    }, [isOpen]);

    // If modal is closed, don't render anything
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 w-full max-w-2xl h-[600px] rounded-xl shadow-xl flex flex-col">
                {/* Modal Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Bot className="h-6 w-6 text-blue-500" />
                        <h2 className="text-xl font-dm-serif font-semibold text-gray-900 dark:text-white">JPRO</h2>
                    </div>
                    <button 
                        onClick={() => setIsOpen(false)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Chat Content Area */}
                <div className="flex-1 overflow-y-auto p-4">
                    {messages.map((message, index) => (
                        <div
                            key={message.id}
                            className={`flex ${message.isUser ? 'justify-end' : 'justify-start'} mb-4`}
                        >
                            <div
                                className={`max-w-xs p-4 rounded-lg ${
                                    message.isUser
                                        ? 'font-rasa bg-blue-500 text-white text-lg'
                                        : 'font-rasa bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white text-lg border border-gray-200 dark:border-gray-600'
                                }`}
                            >
                                {message.isUser ? (
                                    message.text
                                ) : (
                                    <div className="flex flex-col">
                                        {index === 0 ? (
                                            <>
                                                {message.text.split('Terms & Conditions')[0]}
                                                <Link to='/terms' className="text-blue-500 hover:underline">
                                                    Terms & Conditions
                                                </Link>
                                            </>
                                        ) : (
                                            <div
                                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatText(message.text), {
                                                    ALLOWED_TAGS: ['strong', 'em', 'br', 'div', 'span', 'p'],
                                                    ALLOWED_ATTR: ['style', 'class']
                                                }) }}
                                                style={{ whiteSpace: 'pre-line' }}
                                            />
                                        )}
                                        
                                        {!message.isUser && index > 0 && (
                                            <button
                                                onClick={() => handleAudio(message.id, message.text)}
                                                className="self-end mt-2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400"
                                                title={
                                                    audioStates[message.id]?.status === 'loading' 
                                                        ? "Loading audio..." 
                                                        : audioStates[message.id]?.status === 'playing'
                                                            ? "Pause" 
                                                            : "Play message"
                                                }
                                                disabled={audioStates[message.id]?.status === 'loading'}
                                            >
                                                {getAudioIcon(message.id)}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start mb-4">
                            <div className="max-w-xs p-3 rounded-lg bg-gray-100 dark:bg-gray-800">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Invisible element for auto-scrolling */}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2">
                        <button
                            onClick={isRecording ? stopRecording : startRecording}
                            className={`p-2 rounded-lg ${
                                isRecording 
                                    ? 'bg-red-500 hover:bg-red-600' 
                                    : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600'
                            } transition-colors`}
                            title={isRecording ? "Stop recording" : "Start recording"}
                        >
                            {isRecording ? <StopCircle size={20} /> : <Mic size={20} />}
                        </button>
                        
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={(e) => setInputMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            placeholder="Type your message..."
                            className="flex-1 px-4 py-2 border bg-white border-gray-300 text-gray-700  dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                        
                        <button
                            onClick={handleSendMessage}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            disabled={isLoading}
                        >
                            Send
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ChatModal;